import Sidebar from "../Sidebar";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import MediaContext, {micTestLabelKey, micTestStatusKey} from "../../context/MediaContext";
import AppContext from "../../context/AppContext";
import ChevronIcon from '../icons/svg/dropdown-chevron.svg?react';
import InfoIcon from '../icons/svg/info.svg?react';
import CheckmarkIcon from '../icons/svg/checkmark.svg?react';
import CrossIcon from '../icons/svg/cross.svg?react';
import dropdownChevron from '../icons/svg/dropdown-chevron.svg';
import checkboxIcon from '../icons/svg/checkbox.svg';
import {getLanguageName} from '../../lib/languageNames';
import MicLevel from "../general/MicLevel";
import SpeakerLevel from "../general/SpeakerLevel";
import NotificationContext from "../../context/NotificationContext";
import {savePreferences, PreferencesBody} from "../../lib/preferences";

import './SidebarDeviceSettings.scss'

export default () => {
  const {t} = useTranslation()
  const { userData, conferenceStatus, ws, users } = useContext(AppContext)
  const { createNotification, hideNotification } = useContext(NotificationContext)
  const isTeilnehmer = userData ? !userData.isTranslator : false
  const isSim = conferenceStatus.translationType === '2'

  const assignedUuids = conferenceStatus.userPreferences?.assignedTranslatorUuids ?? []
  const assignedDebug = users.filter(u => assignedUuids.includes(u.uuid))
  const mutedDebug = users.filter(u => u.isTranslator && !u.isModerator && !assignedUuids.includes(u.uuid))
  const spkFilter = conferenceStatus.userPreferences?.speakersToFilter ?? []
  const filteredDebug = users.filter(u => spkFilter.includes(u.uuid))
  const conferenceLanguages = conferenceStatus.conferenceLanguages ?? []

  const originalPrefs = conferenceStatus.userPreferences

  const [localSpokenLanguage, setLocalSpokenLanguage] = useState<string | null>(originalPrefs?.spokenLanguage ?? null)
  const [localNeededLanguages, setLocalNeededLanguages] = useState<string[]>(originalPrefs?.neededLanguages ?? [])
  const [localSimultanConfig, setLocalSimultanConfig] = useState<number | null>(originalPrefs?.simultanConfig ?? null)
  const [saving, setSaving] = useState(false)

  const isDirty = localSpokenLanguage !== (originalPrefs?.spokenLanguage ?? null)
    || JSON.stringify(localNeededLanguages) !== JSON.stringify(originalPrefs?.neededLanguages ?? [])
    || localSimultanConfig !== (originalPrefs?.simultanConfig ?? null)

  // `?? []`: an older backend omits the field, whatever the type says.
  const missingInterpreters = (!originalPrefs || isDirty) ? [] : (originalPrefs.missingInterpreters ?? [])

  const currentSpokenLanguage = localSpokenLanguage
  const simultanConfig = localSimultanConfig

  const toggleNeededLanguage = (lang: string) => {
    setLocalNeededLanguages(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    )
    if (localSpokenLanguage === lang) setLocalSpokenLanguage(null)
  }

  const handleSpokenLanguage = (lang: string) => {
    setLocalSpokenLanguage(lang)
    setLocalNeededLanguages(prev => prev.filter(l => l !== lang))
  }

  const handleSavePreferences = async () => {
    if (!ws?.token) return
    setSaving(true)
    try {
      const body: PreferencesBody = { spokenLanguage: localSpokenLanguage, neededLanguages: localNeededLanguages }
      if (localSimultanConfig !== null) body.simultanConfig = localSimultanConfig
      await savePreferences(ws, body)
      // Saved now — clear any earlier failure notice, including the persistent one the green room
      // raises when the join-time save failed.
      hideNotification('prefs-save-failed')
    } catch (e) {
      // RISKS.md/P1.20: surface the failure, and do NOT reset the local state — leaving it dirty is
      // what keeps "Save" actionable so the user can retry.
      console.error('Failed to save preferences', e)
      // Not redundant: a still-showing join-time notice shares this id and createNotification
      // de-dupes (first wins), so without this the user keeps reading "change them under
      // Settings > Interpreter" while standing in that very panel.
      hideNotification('prefs-save-failed')
      createNotification({
        id: 'prefs-save-failed',
        message: t('chat.prefsSaveFailed'),
        ttl: 10000,
      })
    }
    setSaving(false)
  }

  const [videoOpen, setVideoOpen] = useState(true)
  const [audioOpen, setAudioOpen] = useState(true)
  const [interpreterOpen, setInterpreterOpen] = useState(true)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [backgroundOpen, setBackgroundOpen] = useState(false)
  const [micOpen, setMicOpen] = useState(false)
  const [speakerOpen, setSpeakerOpen] = useState(false)
  const [spokenLangOpen, setSpokenLangOpen] = useState(false)
  const spokenLangRef = useRef<HTMLDivElement>(null)
  const cameraRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const micRef = useRef<HTMLDivElement>(null)
  const speakerRef = useRef<HTMLDivElement>(null)

  const {
    cameras,
    microphones,
    speakers,
    selectedCamera, setSelectedCamera,
    selectedMicrophone, setSelectedMicrophone,
    selectedSpeaker, setSelectedSpeaker,
    selectedBackground, setSelectedBackground,
    // RISKS.md/P2.28: use these, never a local `length === 0` check — a blocked device stays
    // enumerated with its id stripped, so the list is non-empty and the dropdown opens over blank rows.
    cameraUnavailable, micUnavailable, speakerSelectionUnavailable, speakerDeviceMissing,
    testSpeaker, speakerTesting,
    testMicrophone, cancelMicTest, micTestState,
    loadDevices,
  } = useContext(MediaContext)

  // `cancelMicTest`, not `testMicrophone` — that finishes the take and plays it (RISKS.md/P2.55).
  useEffect(() => () => cancelMicTest(), [audioOpen, cancelMicTest])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (cameraRef.current && !cameraRef.current.contains(e.target as Node)) {
        setCameraOpen(false)
      }
      if (backgroundRef.current && !backgroundRef.current.contains(e.target as Node)) {
        setBackgroundOpen(false)
      }
      if (micRef.current && !micRef.current.contains(e.target as Node)) {
        setMicOpen(false)
      }
      if (speakerRef.current && !speakerRef.current.contains(e.target as Node)) {
        setSpeakerOpen(false)
      }
      if (spokenLangRef.current && !spokenLangRef.current.contains(e.target as Node)) {
        setSpokenLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setTimeout(loadDevices, 0)
    const intervalLoadDevices = setInterval(loadDevices, 5000)
    return () => clearInterval(intervalLoadDevices)
  }, [])

  return (
    <Sidebar type="device-settings" title={t('sidebar.settings.title')}>
      <div className="frame-59">
        <div className="frame-56">
          <div className="frame-55" onClick={() => setVideoOpen(o => !o)}>
            <span className="section-title">{t('sidebar.settings.video')}</span>
            <ChevronIcon className={`section-chevron${videoOpen ? ' section-chevron--open' : ''}`}/>
          </div>

          {videoOpen && (
            <>
              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.camera')}</span>
                </div>
                <div
                  className={`dropdown-input${cameraOpen ? ' dropdown-input--open' : ''}${cameraUnavailable ? ' dropdown-input--disabled' : ''}`}
                  ref={cameraRef}
                  onClick={() => !cameraUnavailable && setCameraOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {cameraUnavailable ? t('lobby.none') : (cameras.find(c => c.deviceId === selectedCamera)?.label || t('conferenceInfo.selectPlaceholder'))}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {cameraOpen && (
                    <div className="dropdown-options">
                      {cameras.map(camera => (
                        <div
                          key={camera.deviceId}
                          className={`dropdown-option${selectedCamera === camera.deviceId ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedCamera(camera.deviceId); setCameraOpen(false) }}
                        >
                          {camera.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.background')}</span>
                </div>
                <div
                  className={`dropdown-input${backgroundOpen ? ' dropdown-input--open' : ''}`}
                  ref={backgroundRef}
                  onClick={() => setBackgroundOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {t(`lobby.backgroundOptions.${selectedBackground}`)}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {backgroundOpen && (
                    <div className="dropdown-options">
                      {(['none', 'blur', 'hide'] as const).map(option => (
                        <div
                          key={option}
                          className={`dropdown-option${selectedBackground === option ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedBackground(option); setBackgroundOpen(false) }}
                        >
                          {t(`lobby.backgroundOptions.${option}`)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="frame-56">
          <div className="frame-55" onClick={() => setAudioOpen(o => !o)}>
            <span className="section-title">{t('sidebar.settings.audio')}</span>
            <ChevronIcon className={`section-chevron${audioOpen ? ' section-chevron--open' : ''}`}/>
          </div>

          {audioOpen && (
            <>
              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.speaker')}</span>
                  <div className="test-controls">
                    {speakerTesting && <span className="test-status">{t('lobby.speakerTesting')}</span>}
                    <button
                      type="button"
                      className="device-test-btn"
                      onClick={testSpeaker}
                      disabled={speakerDeviceMissing && !speakerTesting}
                    >
                      {t('lobby.speakerTest')}
                    </button>
                  </div>
                </div>
                <div
                  className={`dropdown-input${speakerOpen ? ' dropdown-input--open' : ''}${speakerSelectionUnavailable ? ' dropdown-input--disabled' : ''}`}
                  ref={speakerRef}
                  onClick={() => !speakerSelectionUnavailable && setSpeakerOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {speakerDeviceMissing
                      ? t('lobby.none')
                      : (speakerSelectionUnavailable || !selectedSpeaker
                        ? t('lobby.systemDefault')
                        : (speakers.find(s => s.deviceId === selectedSpeaker)?.label || t('lobby.systemDefault')))}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {speakerOpen && (
                    <div className="dropdown-options">
                      {speakers.map(speaker => (
                        <div
                          key={speaker.deviceId}
                          className={`dropdown-option${selectedSpeaker === speaker.deviceId ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedSpeaker(speaker.deviceId); setSpeakerOpen(false) }}
                        >
                          {speaker.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* NOT `speakerSelectionUnavailable`: a microphone block strips output ids, so the
                    choice goes while the sound keeps playing (RISKS.md/P2.26, P2.54). */}
                {!speakerDeviceMissing && <SpeakerLevel/>}
              </div>

              <div className="settings-col">
                <div className="text-with-info">
                  <span className="field-label">{t('lobby.microphone')}</span>
                  <div className="test-controls">
                    {micTestStatusKey(micTestState) && (
                      <span className="test-status">{t(micTestStatusKey(micTestState) as string)}</span>
                    )}
                    <div className="device-test-btn-wrap">
                      <button
                        type="button"
                        className="device-test-btn"
                        onClick={testMicrophone}
                        disabled={micUnavailable && micTestState === 'idle'}
                        aria-describedby={micTestState === 'idle' ? 'mic-test-mute-hint' : undefined}
                      >
                        {t(micTestLabelKey(micTestState))}
                      </button>
                      {/* Sidebar only — the green room has no peers to be inaudible to. Unmounted
                          while testing: the pointer is still on the button, and the toast then
                          carries the same sentence (RISKS.md/P2.55). */}
                      {micTestState === 'idle' && (
                        <span className="device-test-tooltip" id="mic-test-mute-hint" role="tooltip">
                          {t('lobby.microphoneTestMuteHint')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className={`dropdown-input${micOpen ? ' dropdown-input--open' : ''}${micUnavailable ? ' dropdown-input--disabled' : ''}`}
                  ref={micRef}
                  onClick={() => !micUnavailable && setMicOpen(o => !o)}
                >
                  <span className="dropdown-value">
                    {micUnavailable ? t('lobby.none') : (microphones.find(m => m.deviceId === selectedMicrophone)?.label || t('conferenceInfo.selectPlaceholder'))}
                  </span>
                  <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                  {micOpen && (
                    <div className="dropdown-options">
                      {microphones.map(mic => (
                        <div
                          key={mic.deviceId}
                          className={`dropdown-option${selectedMicrophone === mic.deviceId ? ' dropdown-option--selected' : ''}`}
                          onClick={e => { e.stopPropagation(); setSelectedMicrophone(mic.deviceId); setMicOpen(false) }}
                        >
                          {mic.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Do not re-add an `audioEnabled` gate: self-mute must look like an OS mute (RISKS.md/P2.51). */}
                {!micUnavailable && <MicLevel/>}
              </div>
            </>
          )}
        </div>
        {isTeilnehmer && (
          <div className="frame-25">
            <div className="frame-55" onClick={() => setInterpreterOpen(o => !o)}>
              <span className="section-title">{t('sidebar.settings.interpreter')}</span>
              <ChevronIcon className={`section-chevron${interpreterOpen ? ' section-chevron--open' : ''}`}/>
            </div>

            {interpreterOpen && <>
            <div className="settings-col">
              <div className="text-with-info text-with-info--wide">
                <span className="field-label">{t('conferenceInfo.spokenLanguage')}</span>
                <div className="sidebar-info-icon">
                  <InfoIcon/>
                  <div className="sidebar-info-tooltip">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                      <path d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z" fill="#212629"/>
                    </svg>
                    <div className="sidebar-info-tooltip__frame">{t('lobby.spokenLanguageInfo')}</div>
                  </div>
                </div>
              </div>
              <div
                className={`dropdown-input${spokenLangOpen ? ' dropdown-input--open' : ''}${conferenceLanguages.length === 0 ? ' dropdown-input--disabled' : ''}`}
                ref={spokenLangRef}
                onClick={() => conferenceLanguages.length > 0 && setSpokenLangOpen(o => !o)}
              >
                <span className={`dropdown-value${!currentSpokenLanguage ? ' dropdown-value--placeholder' : ''}`}>
                  {currentSpokenLanguage ? getLanguageName(currentSpokenLanguage) : t('conferenceInfo.selectPlaceholder')}
                </span>
                <img src={dropdownChevron} alt="" aria-hidden="true" className="dropdown-chevron"/>
                {spokenLangOpen && (
                  <div className="dropdown-options">
                    {conferenceLanguages.map(lang => (
                      <div
                        key={lang}
                        className={`dropdown-option${currentSpokenLanguage === lang ? ' dropdown-option--selected' : ''}`}
                        onClick={e => { e.stopPropagation(); handleSpokenLanguage(lang); setSpokenLangOpen(false) }}
                      >
                        {getLanguageName(lang)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="settings-col">
              <div className="text-with-info">
                <span className="field-label">{t('conferenceInfo.neededLanguages')}</span>
              </div>
              <div className="sidebar-lang-chips">
                {conferenceLanguages
                  .filter(lang => lang !== currentSpokenLanguage)
                  .map(lang => (
                    <button
                      key={lang}
                      type="button"
                      className={`sidebar-lang-chip${localNeededLanguages.includes(lang) ? ' sidebar-lang-chip--active' : ''}`}
                      onClick={() => toggleNeededLanguage(lang)}
                    >
                      {getLanguageName(lang)}
                    </button>
                  ))}
              </div>
            </div>
            {localNeededLanguages.length > 0 && isSim && (
            <div className="settings-col">
              <div className="text-with-info text-with-info--wide">
                <span className="field-label">{t('conferenceInfo.simultanConfig')}</span>
                <div className="sidebar-info-icon">
                  <InfoIcon/>
                  <div className="sidebar-info-tooltip">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="6" viewBox="0 0 14 6" fill="none">
                      <path d="M6.92773 0L13.8559 6H-0.000468731L6.92773 0Z" fill="#212629"/>
                    </svg>
                    <div className="sidebar-info-tooltip__frame sidebar-info-tooltip__frame--wide">{t('lobby.simultanConfigInfo')}</div>
                  </div>
                </div>
              </div>
              <div className="sidebar-simutan-section">
                <label className="sidebar-simutan-label">
                  <input type="radio" name="simultanConfig" checked={simultanConfig === 1} onChange={() => setLocalSimultanConfig(1)} className="sidebar-simutan-input"/>
                  <span className="sidebar-simutan-icon">
                    {simultanConfig === 1
                      ? <img src={checkboxIcon} alt="" aria-hidden="true"/>
                      : <span className="sidebar-simutan-empty"/>
                    }
                  </span>
                  <span className="sidebar-simutan-text">{t('conferenceInfo.simultanReduce')}</span>
                </label>
                <label className="sidebar-simutan-label">
                  <input type="radio" name="simultanConfig" checked={simultanConfig === 2} onChange={() => setLocalSimultanConfig(2)} className="sidebar-simutan-input"/>
                  <span className="sidebar-simutan-icon">
                    {simultanConfig === 2
                      ? <img src={checkboxIcon} alt="" aria-hidden="true"/>
                      : <span className="sidebar-simutan-empty"/>
                    }
                  </span>
                  <span className="sidebar-simutan-text">{t('conferenceInfo.simultanMute')}</span>
                </label>
              </div>
            </div>
            )}
            {isDirty && (
              <div className="sidebar-action-btns">
                <button
                  className="sidebar-action-btn sidebar-action-btn--save"
                  onClick={handleSavePreferences}
                  disabled={saving}
                  title={t('sidebar.settings.save')}
                >
                  <CheckmarkIcon/>
                </button>
                <button
                  className="sidebar-action-btn sidebar-action-btn--reset"
                  onClick={() => {
                    setLocalSpokenLanguage(originalPrefs?.spokenLanguage ?? null)
                    setLocalNeededLanguages(originalPrefs?.neededLanguages ?? [])
                    setLocalSimultanConfig(originalPrefs?.simultanConfig ?? null)
                  }}
                  disabled={saving}
                  title={t('sidebar.settings.reset')}
                >
                  <CrossIcon/>
                </button>
              </div>
            )}
            </>}
          </div>
        )}

      </div>

      {missingInterpreters.length > 0 ? (
        <div className="sidebar-missing-interpreters">
          <p className="sidebar-missing-interpreters__title">Für folgende Sprachen wurden keine Dolmetscher:innen gefunden:</p>
          <ul className="sidebar-missing-interpreters__list">
            {missingInterpreters.map(lang => (
              <li key={lang}>{getLanguageName(lang)}</li>
            ))}
          </ul>
        </div>
      ) : <></>}

      {/* ⚠ Testing scaffolding, not part of the final app — it exists so an attendee can confirm the
          interpreter assignment, the same job the deleted `/info` page did (RISKS.md/P1.22), and it
          goes the same way. `isTeilnehmer` is everyone but interpreters — moderators included — and it
          prints peers' email addresses. Don't build on it. */}
      {isTeilnehmer ? (
        <div className="debug-section">
          <div className="debug-label">DEBUG — Assigned Dolmetscher ({assignedDebug.length})</div>
          {assignedDebug.length === 0
            ? <div className="debug-empty">–</div>
            : assignedDebug.map(u => (
              <div key={u.uuid} className="debug-user">{u.name || u.uuid} <span>({u.email})</span></div>
            ))}

          <div className="debug-label" style={{marginTop: 12}}>Muted Dolmetscher ({mutedDebug.length})</div>
          {mutedDebug.length === 0
            ? <div className="debug-empty">–</div>
            : mutedDebug.map(u => (
              <div key={u.uuid} className="debug-user debug-user--muted">{u.name || u.uuid} <span>({u.email})</span></div>
            ))}

          <div className="debug-label" style={{marginTop: 12}}>Speakers to filter ({filteredDebug.length})</div>
          {filteredDebug.length === 0
            ? <div className="debug-empty">–</div>
            : filteredDebug.map(u => (
              <div key={u.uuid} className="debug-user debug-user--muted">{u.name || u.uuid}</div>
            ))}
        </div>
      ) : <></>}
    </Sidebar>
  )
}